import { SxProps, Theme } from '@mui/material';
import React, { useCallback, useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ServiceTitanJob } from '_lib/api/apiTypes.shim';
import { Typography } from '_lib/components/Typography/Typography';
import { SearchSelect } from '_lib/featureComponents/SearchSelect/SearchSelect';

import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

export interface JobNumberSearchSelectProps extends FieldIssueProps {
  job: ServiceTitanJob | null;
  onSetJob: (job: ServiceTitanJob | null) => Promise<boolean>;
  companyId: string;
  currentProviderCustomerId?: string;
  required?: boolean;
  sx?: SxProps<Theme>;
}

const toJobNoAndStatus = (job: ServiceTitanJob) => `${job.jobNo} - ${job.status}`;

export const JobNumberSearchSelect = (props: JobNumberSearchSelectProps) => {
  const { job, onSetJob, companyId, currentProviderCustomerId, required, issuePath, issues, sx } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      const res = await serviceTitanApi.jobs.list({ params: { jobNo: searchValue } });
      return res.results;
    },
    [serviceTitanApi.jobs]
  );

  return (
    <SearchSelect
      doSearch={handleSearch}
      selectedOption={job}
      trySetSelectedOption={onSetJob}
      getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : toJobNoAndStatus(option))}
      renderListOption={(option, liProps) => (
        <li {...liProps} key={option.providerId}>
          <Typography
            variant='body1Bold'
            text={`${toJobNoAndStatus(option)}${currentProviderCustomerId && currentProviderCustomerId !== option.providerCustomerId ? ' (different customer)' : ''}`}
          />
        </li>
      )}
      label='Job'
      placeHolder='Job Number'
      required={required}
      numericInputOnly
      issuePath={issuePath}
      issues={issues}
      sx={sx}
    />
  );
};
